import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

const API = (typeof window !== 'undefined' && (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'))
  ? 'http://localhost:8001/api' 
  : '/api';

function SimpleTradingDashboard() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [balance, setBalance] = useState(0);
  const [favoriteStocks, setFavoriteStocks] = useState([]);
  const [topGainers, setTopGainers] = useState([]);

  useEffect(() => {
    fetchUserData();
    fetchTopGainers();
  }, []);

  const fetchUserData = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/auth/me`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setUser(data);
        setBalance(data.balance || 0);
        
        // Fetch favorite stocks if user has favorites
        if (data.favorites && data.favorites.length > 0) {
          fetchFavoriteStocks(data.favorites);
        }
      }
    } catch (err) {
      console.error('Failed to fetch user:', err);
    }
  };

  const fetchFavoriteStocks = async (favoriteIds) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/assets`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const assets = await response.json();
        const favorites = assets.filter(asset => favoriteIds.includes(asset.id));
        setFavoriteStocks(favorites.slice(0, 5)); // Max 5
      }
    } catch (err) {
      console.error('Failed to fetch favorites:', err);
    }
  };

  const fetchTopGainers = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/assets`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const assets = await response.json();
        // BIST hisselerini al ve günlük değişime göre sırala
        const sorted = assets
          .filter(a => a.category === 'BIST_100')
          .filter(a => a.change_percent > 0 && a.change_percent <= 15) // Sadece veritabanındaki değeri kullan
          .sort((a, b) => (b.change_percent || 0) - (a.change_percent || 0));
        setTopGainers(sorted.slice(0, 5));
      }
    } catch (err) {
      console.error('Failed to fetch top gainers:', err);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    window.location.href = '/';
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-900 via-gray-800 to-gray-900">
      {/* Header */}
      <div className="bg-gray-900/50 backdrop-blur-lg border-b border-gray-700">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-purple-500 rounded-lg flex items-center justify-center">
              <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
              </svg>
            </div>
            <div>
              <h1 className="text-sm font-bold text-white">Trading Platform</h1>
              <p className="text-[10px] text-gray-400">{user?.email}</p>
            </div>
          </div>
          <button
            onClick={handleLogout}
            className="text-gray-400 hover:text-white transition-colors p-1"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
            </svg>
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 py-4">
        {/* Investment Section */}
        <div className="mb-4">
          <p className="text-gray-400 text-xs mb-1">Yatırım</p>
          <div className="flex items-baseline gap-2 mb-4">
            <span className="text-2xl font-bold text-white">₺{balance.toLocaleString('tr-TR', { minimumFractionDigits: 2 })}</span>
          </div>

          {/* Action Buttons */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-4">
            <button 
              onClick={() => navigate('/deposit')}
              className="bg-gray-800/50 hover:bg-gray-800 border border-gray-700 rounded-lg px-3 py-2.5 flex flex-col items-center gap-1 transition-all"
            >
              <svg className="w-4 h-4 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              <span className="text-white text-xs font-medium">Yatır</span>
            </button>
            <button 
              onClick={() => navigate('/withdraw')}
              className="bg-gray-800/50 hover:bg-gray-800 border border-gray-700 rounded-lg px-3 py-2.5 flex flex-col items-center gap-1 transition-all"
            >
              <svg className="w-4 h-4 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
              </svg>
              <span className="text-white text-xs font-medium">Çek</span>
            </button>
            <button 
              onClick={() => navigate('/trading')}
              className="bg-blue-600 hover:bg-blue-700 rounded-lg px-3 py-2.5 flex flex-col items-center gap-1 transition-all"
            >
              <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
              </svg>
              <span className="text-white text-xs font-medium">Hisse Al</span>
            </button>
            <button 
              onClick={() => navigate('/trading')}
              className="bg-red-600 hover:bg-red-700 rounded-lg px-3 py-2.5 flex flex-col items-center gap-1 transition-all"
            >
              <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 17h8m0 0V9m0 8l-8-8-4 4-6-6" />
              </svg>
              <span className="text-white text-xs font-medium">Hisse Sat</span>
            </button>
          </div>

          {/* Currency Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
            <div className="bg-gradient-to-br from-blue-600/20 to-blue-800/20 border border-blue-500/30 rounded-xl p-4">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 bg-blue-500/20 rounded-full flex items-center justify-center">
                    <span className="text-lg">₺</span>
                  </div>
                  <div>
                    <p className="text-gray-400 text-xs">Türk Lirası</p>
                    <p className="text-white text-sm font-semibold">TRY</p>
                  </div>
                </div>
                <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </div>
              <div className="text-xl font-bold text-white mb-1">
                ₺{balance.toLocaleString('tr-TR', { minimumFractionDigits: 2 })}
              </div>
              <div className="text-xs text-gray-400">Kullanılabilir bakiye</div>
            </div>

            <div className="bg-gradient-to-br from-green-600/20 to-green-800/20 border border-green-500/30 rounded-xl p-4">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 bg-green-500/20 rounded-full flex items-center justify-center">
                    <span className="text-lg">$</span>
                  </div>
                  <div>
                    <p className="text-gray-400 text-xs">Amerikan Doları</p>
                    <p className="text-white text-sm font-semibold">USD</p>
                  </div>
                </div>
                <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </div>
              <div className="text-xl font-bold text-white mb-1">
                $0.00
              </div>
              <div className="text-xs text-gray-400">Kullanılabilir bakiye</div>
            </div>
          </div>

          {/* Lists Section */}
          <div className="bg-gray-800/30 border border-gray-700 rounded-xl p-4">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-base font-bold text-white">Listeler</h2>
            </div>

            <div className="space-y-2">
              {/* Favori Hisselerim */}
              <div className="bg-gray-800/50 border border-gray-700 rounded-lg p-3">
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-8 h-8 bg-yellow-500/20 rounded-lg flex items-center justify-center">
                    <svg className="w-4 h-4 text-yellow-400" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                    </svg>
                  </div>
                  <div className="text-left flex-1">
                    <p className="text-white text-sm font-medium">Favori Hisselerim</p>
                  </div>
                </div>
                {favoriteStocks.length > 0 ? (
                  <div className="space-y-1.5 mt-2">
                    {favoriteStocks.map((stock) => (
                      <button
                        key={stock.id}
                        onClick={() => navigate('/trading')}
                        className="w-full flex items-center justify-between py-2 px-2 hover:bg-gray-700/50 rounded transition-colors"
                      >
                        <span className="text-white text-xs font-medium">{stock.symbol}</span>
                        <span className={`text-xs font-medium ${stock.change_percent >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                          {stock.change_percent >= 0 ? '+' : ''}{stock.change_percent?.toFixed(2)}%
                        </span>
                      </button>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-400 text-xs text-center py-2">Favori hisse seçilmedi</p>
                )}
              </div>

              {/* Artış Gösteren Hisseler */}
              <div className="bg-gray-800/50 border border-gray-700 rounded-lg p-3">
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-8 h-8 bg-green-500/20 rounded-lg flex items-center justify-center">
                    <svg className="w-4 h-4 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                    </svg>
                  </div>
                  <div className="text-left flex-1">
                    <p className="text-white text-sm font-medium">Artış Gösteren Hisseler</p>
                  </div>
                </div>
                {topGainers.length > 0 ? (
                  <div className="space-y-1.5 mt-2">
                    {topGainers.map((stock) => (
                      <button
                        key={stock.id}
                        onClick={() => navigate('/trading')}
                        className="w-full flex items-center justify-between py-2 px-2 hover:bg-gray-700/50 rounded transition-colors"
                      >
                        <span className="text-white text-xs font-medium">{stock.symbol}</span>
                        <span className="text-green-400 text-xs font-medium">
                          +{stock.change_percent?.toFixed(2)}%
                        </span>
                      </button>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-400 text-xs text-center py-2">Yükleniyor...</p>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Navigation */}
      <div className="fixed bottom-0 left-0 right-0 bg-gray-900/95 backdrop-blur-lg border-t border-gray-700 pb-safe">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-center justify-around py-2">
            <button className="flex flex-col items-center gap-0.5 text-blue-400 py-2">
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                <path d="M3 13h8V3H3v10zm0 8h8v-6H3v6zm10 0h8V11h-8v10zm0-18v6h8V3h-8z" />
              </svg>
              <span className="text-[10px] font-medium">Ana Sayfa</span>
            </button>
            
            <button 
              onClick={() => navigate('/trading')}
              className="flex flex-col items-center gap-0.5 text-gray-400 hover:text-white transition-colors py-2"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 12l3-3 3 3 4-4M8 21l4-4 4 4M3 4h18M4 4h16v12a1 1 0 01-1 1H5a1 1 0 01-1-1V4z" />
              </svg>
              <span className="text-[10px] font-medium">Yatırım</span>
            </button>
            
            <button 
              onClick={() => navigate('/search')}
              className="flex flex-col items-center gap-0.5 text-gray-400 hover:text-white transition-colors py-2"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              <span className="text-[10px] font-medium">Ara</span>
            </button>
            
            <button 
              onClick={() => navigate('/history')}
              className="flex flex-col items-center gap-0.5 text-gray-400 hover:text-white transition-colors py-2"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span className="text-[10px] font-medium">Geçmiş</span>
            </button>
            
            <button 
              onClick={() => navigate('/profile')}
              className="flex flex-col items-center gap-0.5 text-gray-400 hover:text-white transition-colors py-2"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
              <span className="text-[10px] font-medium">Profil</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default SimpleTradingDashboard;
